import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import DataTable from "../../../components/shared/DataTable";
import SearchInput from "../../../components/shared/SearchInput";
import Pagination from "../../../components/shared/Pagination";
import { formatDate, formatCurrency, statusBadgeClass } from "../../../utils/helpers";
import api from "../../../utils/api";

export default function PaymentManagement() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");

  const { data, isLoading } = useQuery(
    ["admin-payments", page, search, statusFilter],
    () =>
      api.get(
        `/admin/payments?page=${page}&search=${search}&status=${statusFilter}`
      ),
    { keepPreviousData: true }
  );

  const refundMutation = useMutation(
    (id) => api.post(`/admin/payments/${id}/refund`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("admin-payments");
        toast.success("Refund initiated");
      },
      onError: (err) => toast.error(err.message),
    }
  );

  const cancelMutation = useMutation(
    (id) => api.post(`/admin/payments/${id}/cancel`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("admin-payments");
        toast.success("Payment cancelled");
      },
    }
  );

  const columns = [
    { key: "transactionId", label: "Transaction ID" },
    { key: "patientName", label: "Patient", sortable: true },
    { key: "type", label: "Type" },
    {
      key: "amount",
      label: "Amount",
      render: (row) => formatCurrency(row.amount),
      sortable: true,
    },
    {
      key: "createdAt",
      label: "Date",
      render: (row) => formatDate(row.createdAt),
      sortable: true,
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span className={statusBadgeClass(row.status)}>{row.status}</span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="flex gap-2">
          {row.status === "paid" && (
            <button
              onClick={() => refundMutation.mutate(row.id)}
              className="text-xs px-3 py-1 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200"
            >
              Refund
            </button>
          )}
          {["paid", "pending"].includes(row.status) && (
            <button
              onClick={() => cancelMutation.mutate(row.id)}
              className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
            >
              Cancel
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by patient, transaction ID…"
          className="w-72"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input-field w-auto"
        >
          <option value="all">All</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="refunded">Refunded</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      <DataTable
        columns={columns}
        data={data?.payments || []}
        loading={isLoading}
        emptyMessage="No payments found."
      />
      <Pagination
        page={page}
        totalPages={data?.totalPages || 1}
        onPageChange={setPage}
      />
    </div>
  );
}
